// @ts-strict-ignore
import {
  EXPRESSION_FUNCTION_OPERATORS,
  EXPRESSION_OPERATORS,
  WorkflowImportUpdateResultType, WorkflowTransformationIssueType,
} from './constants';
import type {
  Action,
  ActionOption,
  Expression,
  ExpressionTerm,
  List,
  ObjectIdReverseTransformationMap,
  ObjectIdTransformationMap,
  ServiceAttributesById,
  Services,
} from './types';
import { resolveServiceOrUniversalAttribute } from 'flow/service/thresholds';

export function remapExpressionDataRouteIds({
  expressionArray,
  dataRouteIdMap,
}: {
  expressionArray: Expression;
  dataRouteIdMap: ObjectIdTransformationMap | ObjectIdReverseTransformationMap;
}) {
  if (!expressionArray) {
    return null;
  }

  return expressionArray.map(
    expressionItem => {
      if (Array.isArray(expressionItem)) {
        return remapExpressionDataRouteIds({
          expressionArray: expressionItem,
          dataRouteIdMap,
        });
      }

      if (typeof expressionItem === 'object'
        && expressionItem !== null && expressionItem.data_route_id
        && dataRouteIdMap[expressionItem.data_route_id]) {
        return {
          data_route_id: dataRouteIdMap[expressionItem.data_route_id],
        };
      }

      return expressionItem;
    });
}

export function createRangeOfNumbers(start, stop, step) {
  return Array.from({ length: (stop - start) / (step + 1) }, (_, i) => start + (i * step));
}

export function createStringOfPathArray(array): string {
  return array.reduce((result, path) => {
    if (path.includes(']')) {
      return `${result}[${path}`;
    }

    if (!isNaN(Number(path))) {
      return `${result}[${path}]`;
    }

    return result.length > 0
      ? `${result}.${path}`
      : path;
  }, '');
}

export function remapListIdsForListServiceAction({
  actionOptionValue,
  action,
  lists,
}: {
  actionOptionValue: ActionOption;
  action: Action;
  lists: List[];
}) {
  const listIdOfTheAction = actionOptionValue.value;
  const matchingList = lists.find(list => list.customer_list_id === Number(listIdOfTheAction));

  if (matchingList) {
    return {
      value: matchingList.customer_list_id,
      type: null,
    };
  }

  const matchingListByServiceName = lists.find(list => list.list_name === action.service.name);
  if (matchingListByServiceName) {
    return {
      value: matchingListByServiceName.customer_list_id,
      type: WorkflowImportUpdateResultType.LIST_ID_UPDATED,
    };
  }

  return {
    value: null,
    type: WorkflowTransformationIssueType.NO_MATCHING_LIST_ID_IN_CUSTOMER,
  };
}

export function getAttributeToken({
  action,
  attributeId,
  services,
  universalAttributes,
}: {
  action: Action;
  attributeId: number;
  services: Services;
  universalAttributes: ServiceAttributesById;
}) {
  if (attributeId && action?.service) {
    const matchingServiceObject = Object.values(services).find(
      service => service.internal_name === action.service.internalName);
    const attribute = resolveServiceOrUniversalAttribute(
      matchingServiceObject?.attributes,
      universalAttributes,
      attributeId,
    );
    if (attribute) {
      // sometimes when a workflow is cloned to a "target" customer,
      // certain attributes are unique to the "source" customer
      return attribute.attribute_token;
    }
  }
  return null;
}

/**
 * check if provided string item is one of expression operator strings
 */
export function getIsExpressionOperatorString(item: string) {
  return EXPRESSION_OPERATORS.includes(item);
}

export function getIsExpressionFunctionString(item: string) {
  return EXPRESSION_FUNCTION_OPERATORS.includes(item);
}

export function getExpressionDataRouteIds(expression: Expression): string[] {
  if (!expression) {
    return [];
  }

  return expression.flatMap(expressionItem => {
    if (Array.isArray(expressionItem)) {
      return getExpressionDataRouteIds(expressionItem);
    }

    if (typeof expressionItem === 'object' && expressionItem !== null && expressionItem.data_route_id) {
      return [String(expressionItem.data_route_id)];
    }

    return [];
  });
}

/**
 * Puts a group's min/max back between any operands that ended up next to each other, since that
 * operator is what draws the comma. Safe to call twice, and leaves a trailing operator alone.
 */
export function separateFunctionOperands(
  group: (ExpressionTerm | ExpressionTerm[])[],
  groupFunction: string,
) {
  const isOperator = (item: ExpressionTerm | ExpressionTerm[]) => (
    typeof item === 'string' && getIsExpressionOperatorString(item)
  );

  return group.flatMap((item, index) => {
    const nextItem = group[index + 1];
    const needsSeparator = nextItem !== undefined && !isOperator(item) && !isOperator(nextItem);
    return needsSeparator ? [item, groupFunction] : [item];
  });
}

/**
 * Where the item at index ends up once the group's commas are inserted.
 */
export function getIndexAfterSeparating(
  group: (ExpressionTerm | ExpressionTerm[])[],
  groupFunction: string,
  index: number,
) {
  const isOperator = (item: ExpressionTerm | ExpressionTerm[]) => (
    typeof item === 'string' && getIsExpressionOperatorString(item)
  );
  const separatorsBefore = group.slice(0, index).filter((item, itemIndex) => {
    const nextItem = group[itemIndex + 1];
    return nextItem !== undefined && !isOperator(item) && !isOperator(nextItem);
  }).length;

  return index + separatorsBefore;
}

/**
 * Repairs every min/max group in an expression, however deeply nested.
 */
export function normalizeExpressionFunctionSeparators(expression: Expression): Expression {
  if (!Array.isArray(expression)) return expression;

  const normalized = expression.map(item => (
    Array.isArray(item)
      ? normalizeExpressionFunctionSeparators(item as Expression)
      : item
  )) as Expression;

  const groupFunction = getExpressionFunction(normalized);

  if (!groupFunction) return normalized;

  // removing an argument can leave two commas next to each other, and one is enough
  const collapsed = normalized.filter((item, index) => (
    item !== groupFunction || normalized[index - 1] !== groupFunction
  )) as Expression;

  // min/max sits between its operands, so one in front has nothing on its left and evaluates to null
  const leadsWithSeparator = collapsed[0] === groupFunction
    && !getIsExpressionGroupAwaitingOperands(collapsed);

  if (!leadsWithSeparator) {
    return separateFunctionOperands(collapsed, groupFunction) as Expression;
  }

  const remaining = collapsed.slice(1) as Expression;
  // that operator is the only record of min vs max, so keep one when it was the group's last
  const repaired = remaining.includes(groupFunction)
    ? remaining
    : [...remaining, groupFunction] as Expression;

  return separateFunctionOperands(repaired, groupFunction) as Expression;
}

/**
 * Removes the trailing min/max from every group except the one at keepOpen. That trailing operator is
 * what draws the comma inviting another value, so dropping it "closes" the group.
 *
 * A group's last operator is always kept, because nothing else records whether it is min or max.
 */
export function settleFunctions(
  expression: Expression,
  options: { keepOpen?: string } = {},
  path = '',
): Expression {
  if (!Array.isArray(expression)) return expression;

  const { keepOpen } = options;

  const settled = expression.map((item, index) => (
    Array.isArray(item)
      ? settleFunctions(item as Expression, options, `${path}[${index}]`)
      : item
  )) as Expression;

  const groupFunction = getExpressionFunction(settled);

  if (!groupFunction || path === keepOpen || settled.at(-1) !== groupFunction) return settled;

  const remaining = settled.slice(0, -1) as Expression;

  return getExpressionFunction(remaining) ? remaining : settled;
}

/**
 * Which index backspace removes from a min/max group (its last argument) and where the caret then
 * belongs. Returns undefined once no arguments are left, and the caller deletes the group instead.
 */
export function getFunctionPeelIndices(expression: Expression) {
  const groupFunction = getExpressionFunction(expression);

  if (!groupFunction) return undefined;

  const getIsOperand = (item: ExpressionTerm | ExpressionTerm[]) => item !== groupFunction;
  const peelIndex = expression.findLastIndex(getIsOperand);

  if (peelIndex < 0) return undefined;

  return {
    peelIndex,
    // commas have no caret position, so the caret goes to the argument, not past it
    previousOperandIndex: expression.slice(0, peelIndex).findLastIndex(getIsOperand),
  };
}

/**
 * True when a group contains no operators, which means its contents can be moved into the parent
 * without changing the result.
 */
export function getIsExpressionGroupWithoutOperators(expression: Expression) {
  return Array.isArray(expression) && !expression.some(item => (
    typeof item === 'string' && getIsExpressionOperatorString(item)
  ));
}

/**
 * True when a group has no arguments yet: either it is empty, or it holds only the min/max it was
 * opened with.
 */
export function getIsExpressionGroupAwaitingOperands(expression: Expression) {
  return Array.isArray(expression)
    && expression.every(item => typeof item === 'string' && getIsExpressionFunctionString(item));
}

/**
 * Returns 'min' or 'max' if that operator separates the group's arguments. Returns undefined if the
 * group has neither, or mixes both.
 */
export function getExpressionFunction(expression: Expression) {
  const separators = expression.filter(item => (
    typeof item === 'string' && getIsExpressionFunctionString(item)
  )) as string[];

  if (!separators.length || separators.some(separator => separator !== separators[0])) {
    return undefined;
  }

  /*
    Operators evaluate left to right, so anything after the last min/max applies to the whole fold:
    [a, 'max', b, '/', c] is max(a, b) / c, which cannot be drawn as Max ( a , b / c ).
  */
  const lastSeparatorIndex = expression.findLastIndex(item => (
    typeof item === 'string' && getIsExpressionFunctionString(item)
  ));
  const hasOperatorAfterFold = expression.slice(lastSeparatorIndex + 1).some(item => (
    typeof item === 'string' && getIsExpressionOperatorString(item)
  ));

  return hasOperatorAfterFold ? undefined : separators[0];
}
